import React, { useCallback } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { 
  SliderControl, 
  SelectControl,
  SwitchControl,
} from '../../core/NodeControls';

/**
 * MultiHeadAttentionNode represents a transformer-based attention layer in the neural network
 */
const MultiHeadAttentionNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Heads: {data.num_heads || 8}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          <Chip 
            label={`key dim: ${data.key_dim || 64}`}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
            }}
          />
          
          {data.value_dim && (
            <Chip 
              label={`val dim: ${data.value_dim}`}
              size="small"
              sx={{ 
                fontSize: '0.7rem',
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
              }}
            />
          )}
        </Box>
        
        {/* Visual representation of multi-head attention */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {Array.from({ length: Math.min(data.num_heads || 8, 8) }).map((_, index) => (
            <Box 
              key={index}
              sx={{ 
                width: 8,
                height: 16,
                backgroundColor: theme.palette.warning.main,
                opacity: 0.3 + (index / 20), // Slight variation in opacity
                borderRadius: 1,
              }} 
            />
          ))}
        </Box>
      </>
    );
  };
  
  // Controls for the node configuration popover
  const renderControls = () => {
    return (
      <>
        <SliderControl
          value={data.num_heads || 8}
          onChange={(value) => handleDataChange('num_heads', value)}
          min={1}
          max={16}
          step={1}
          label="Number of Heads"
          tooltip="Number of attention heads"
          marks
        />
        
        <SliderControl
          value={data.key_dim || 64}
          onChange={(value) => handleDataChange('key_dim', value)}
          min={8}
          max={512}
          step={8}
          label="Key Dimension"
          tooltip="Size of each attention head for key"
          marks
        />
        
        <SliderControl
          value={data.value_dim || data.key_dim || 64}
          onChange={(value) => handleDataChange('value_dim', value)}
          min={8}
          max={512}
          step={8}
          label="Value Dimension"
          tooltip="Size of each attention head for value"
          marks
        />
        
        <SelectControl
          value={data.dropout || 0}
          onChange={(value) => handleDataChange('dropout', parseFloat(value))}
          options={[
            { value: 0, label: 'None' },
            { value: 0.1, label: '10%' },
            { value: 0.2, label: '20%' },
            { value: 0.3, label: '30%' },
            { value: 0.4, label: '40%' },
            { value: 0.5, label: '50%' },
          ]}
          label="Dropout"
          tooltip="Fraction of the input units to drop"
        />
        
        <SwitchControl
          checked={data.use_bias !== false}
          onChange={(value) => handleDataChange('use_bias', value)}
          label="Use Bias"
          tooltip="Whether the layer uses a bias vector"
        />
        
        <SelectControl
          value={data.attention_type || 'scaled_dot_product'}
          onChange={(value) => handleDataChange('attention_type', value)}
          options={[
            { value: 'scaled_dot_product', label: 'Scaled Dot-Product' },
            { value: 'dot_product', label: 'Dot-Product' },
            { value: 'additive', label: 'Additive' },
          ]}
          label="Attention Type"
          tooltip="Type of attention mechanism to use"
        />
        
        <SwitchControl
          checked={data.causal || false}
          onChange={(value) => handleDataChange('causal', value)}
          label="Causal Attention"
          tooltip="Whether to apply causal mask to prevent attending to future tokens"
        />
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<VisibilityIcon fontSize="small" />}
      nodeType="attention"
      preview={renderPreview()}
      controls={renderControls()}
      multipleInputs={true}  // Can receive queries, keys, values as separate inputs
      multipleOutputs={false}
      onDelete={data.onDelete}
    />
  );
};

export default memo(MultiHeadAttentionNode); 